import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';

export interface RepeatableRowsProps<T> {
  readonly rows: readonly T[];
  /** The fields of one row; `index` is its position. */
  readonly renderRow: (row: T, index: number) => ReactNode;
  readonly onAdd: () => void;
  readonly onRemove: (index: number) => void;
  /** The add button disappears at this many rows (v1: 10 deductible options). */
  readonly max?: number;
  /** Rows cannot be removed below this many (v1: 1). */
  readonly min?: number;
  readonly readOnly?: boolean;
  readonly label: string;
  /** The add button's accessible name and tooltip: "Option hinzufügen". */
  readonly addLabel: string;
  /** A row's remove button: "Option 2 löschen". */
  readonly removeLabel: (index: number) => string;
  /** A message under the rows — a validation error spanning them ("Optionsbeträge müssen eindeutig sein"). */
  readonly error?: string | undefined;
}

/**
 * A short list of rows of the same fields, added and removed in place — the deductible options of
 * `tarif_deckung` (ui-spec §3.3b: 1–10 options, each a value and, for Prozent, a limit). The
 * `playlist_add` button is EIS v1's; the bounds are the caller's.
 */
export function RepeatableRows<T>({
  rows,
  renderRow,
  onAdd,
  onRemove,
  max,
  min = 0,
  readOnly = false,
  label,
  addLabel,
  removeLabel,
  error,
}: RepeatableRowsProps<T>): ReactNode {
  const canAdd = !readOnly && (max === undefined || rows.length < max);
  return (
    <Box role="group" aria-label={label}>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}
      >
        <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {canAdd && (
          <Tooltip title={addLabel}>
            <IconButton size="small" aria-label={addLabel} onClick={onAdd}>
              <PlaylistAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Stack spacing={1}>
        {rows.map((row, index) => (
          // Rows have no identity beyond their position: an option IS its amount, and the amount is
          // what the operator is editing.
          <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>{renderRow(row, index)}</Box>
            {!readOnly && rows.length > min && (
              <Tooltip title={removeLabel(index)}>
                <IconButton
                  size="small"
                  aria-label={removeLabel(index)}
                  onClick={() => {
                    onRemove(index);
                  }}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ))}
      </Stack>
      {error !== undefined && (
        <Typography variant="caption" color="error" role="alert" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
